import { ApiChannel, ApiProject } from "@incendium/api";
import {
  Badge,
  Button,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { Delete, Reorder } from "@mui/icons-material";
import SpacedList, { SpacedLinkButton } from "Components/UI/SpacedList";
import { usePreviousDistinct, useUpdateEffect } from "react-use";
import React, { useState } from "react";
import { channelService } from "Apis";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import GroupsCell from "./GroupsCell";
import { cell1Icon } from "consts";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type onEditFn = (c: ApiChannel) => void;
type onDeleteFn = (c: ApiChannel) => void;

const StyledBadge = withStyles((theme) => ({
  badge: {
    right: -4,
    top: 12,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
    color: "white",
  },
}))(Badge);

const ChannelRow = ({
  channel,
  onEdit,
  onDelete,
  id,
}: {
  channel: ApiChannel;
  onEdit: onEditFn;
  onDelete: onDeleteFn;
  id: string;
}) => {
  const groups = channel?.channelGroups || [];
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <TableRow
      ref={setNodeRef}
      className={isDragging ? "dragging" : ""}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : undefined,
        background: isDragging ? "#e3f2fd" : undefined,
      }}
      {...attributes}
    >
      <TableCell className="primary" style={{ width: 100 }}>
        <span style={{ display: "flex", alignItems: "center", cursor: "grab" }} {...listeners}>
          <StyledBadge badgeContent={channel.priority} color="secondary">
            <Reorder />
          </StyledBadge>
        </span>
      </TableCell>
      <TableCell style={{ width: 250 }}>
        <SpacedLinkButton onClick={() => onEdit(channel)}>
          {channel.name}
        </SpacedLinkButton>
        {channel.isPaidChannel && (
          <Typography variant="body2">(Is Paid Marketing 💰)</Typography>
        )}
      </TableCell>
      <GroupsCell groups={groups} />

      <TableCell align="right" style={{ width: cell1Icon }}>
        <IconButton
          size="small"
          color="secondary"
          onClick={() => onDelete(channel)}
        >
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const ChannelsList = ({
  project,
  channels,
  setChannels,
  onEdit,
  onDelete,
  onClick,
}: {
  project: ApiProject;
  channels: ApiChannel[];
  setChannels: React.Dispatch<React.SetStateAction<ApiChannel[]>>;
  onEdit: onEditFn;
  onDelete: onDeleteFn;
  onClick: () => void;
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const prevChannels = usePreviousDistinct(channels);
  const [isActive, setIsActive] = useState(false);
  const [isDropping, setIsDrpping] = useState(false);

  useUpdateEffect(() => {
    if (!prevChannels?.length) {
      return;
    }
    // check id order is same or change is note due to new one added
    const prevOrder = prevChannels.map((pr) => `${pr.id}-${pr.priority}`);
    const currentOrder = channels.map((pr) => `${pr.id}-${pr.priority}`);
    if (
      prevOrder.join("-") === currentOrder.join("-") ||
      prevOrder.length < currentOrder.length
    ) {
      return;
    }

    const updateChannels = async () => {
      try {
        await channelService.channelServiceReorderChannels({
          projectId: project.id as number,
          payload: {
            input: channels.map((c) => ({
              channelId: c.id,
              priority: c.priority,
            })),
          },
        });

        enqueueSnackbar("Channels Order Updated", {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      } catch (error) {
        enqueueSnackbar(
          "There was an error updating you channels order, please try again",
          {
            variant: "error",
            autoHideDuration: 2000,
            anchorOrigin: { horizontal: "right", vertical: "top" },
          }
        );
      }
    };

    if (channels.length) {
      updateChannels();
    }
  }, [channels]);

  const updateChannelOrder = (newChannels: ApiChannel[]) => {
    setChannels(
      newChannels
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .map((c, i) => ({
          ...c,
          priority: i + 1,
        }))
    );
  };

  const channelIds = channels.map((c) => String(c.id));

  return (
    <SpacedList
      title="Channels List"
      action={<Button onClick={onClick}>New Channel</Button>}
    >
      <TableHead>
        <TableRow>
          <TableCell>Priority</TableCell>
          <TableCell>Channel Name</TableCell>
          <TableCell>Rules</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={() => setIsActive(true)}
        onDragEnd={event => {
          setIsActive(false);
          setIsDrpping(true);
          setTimeout(() => setIsDrpping(false), 100);
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const oldIndex = channelIds.indexOf(active.id as string);
          const newIndex = channelIds.indexOf(over.id as string);
          if (oldIndex === -1 || newIndex === -1) return;
          // lets reorder channels priority
          const newChannels = arrayMove(channels, oldIndex, newIndex).map(
            (channel, idx) => ({ ...channel, priority: idx + 1 })
          );
          updateChannelOrder(newChannels);
        }}
      >
        <SortableContext items={channelIds} strategy={verticalListSortingStrategy}>
          <TableBody className={clsx({ dragging: isActive, dropping: isDropping })}>
            {channels.map((channel, i) => (
              <ChannelRow
                key={channel.id}
                id={String(channel.id)}
                channel={channel}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </TableBody>
        </SortableContext>
      </DndContext>
    </SpacedList>
  );
};

export default ChannelsList;
